import { ReactNode } from 'react';
import { ColumnsType } from 'antd/lib/table';
import {
  NewClosedProjectsPeriod,
  OptionProps,
  ReportsFilters,
  ReportsSortBy,
} from '@models/interfaces';
import { getTableSortOrder } from '../helpers';
import { isSortByAllowed, renderValue } from './common';

const getNewClosedProjectsSortableColumn = (
  dataIndex: keyof NewClosedProjectsPeriod,
  title: string,
  sortBy: ReportsSortBy,
  filters: ReportsFilters,
  sortByOptions: OptionProps[],
  render?: (value: unknown, record: NewClosedProjectsPeriod) => ReactNode,
): ColumnsType<NewClosedProjectsPeriod>[number] => {
  const isSortable = isSortByAllowed(sortByOptions, sortBy);

  return {
    dataIndex,
    key: sortBy,
    render,
    sorter: isSortable,
    sortOrder:
      isSortable && filters.sortBy === sortBy
        ? getTableSortOrder(filters.sortOrder)
        : undefined,
    title,
  };
};

export const getNewClosedProjectsColumns = (
  filters: ReportsFilters,
  sortByOptions: OptionProps[],
): ColumnsType<NewClosedProjectsPeriod> => [
  getNewClosedProjectsSortableColumn(
    'period',
    'Period',
    ReportsSortBy.Period,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'periodStart',
    key: 'periodStart',
    render: renderValue,
    title: 'Period Start',
  },
  {
    dataIndex: 'periodEnd',
    key: 'periodEnd',
    render: renderValue,
    title: 'Period End',
  },
  getNewClosedProjectsSortableColumn(
    'newCount',
    'New Count',
    ReportsSortBy.NewCount,
    filters,
    sortByOptions,
    renderValue,
  ),
  getNewClosedProjectsSortableColumn(
    'closedCount',
    'Closed Count',
    ReportsSortBy.ClosedCount,
    filters,
    sortByOptions,
    renderValue,
  ),
  getNewClosedProjectsSortableColumn(
    'netChange',
    'Net Change',
    ReportsSortBy.NetChange,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'cumulativeNetChange',
    key: 'cumulativeNetChange',
    render: renderValue,
    title: 'Cumulative Net Change',
  },
  {
    dataIndex: 'previousYearNewCount',
    key: 'previousYearNewCount',
    render: renderValue,
    title: 'Previous Year New Count',
  },
  {
    dataIndex: 'previousYearClosedCount',
    key: 'previousYearClosedCount',
    render: renderValue,
    title: 'Previous Year Closed Count',
  },
  {
    dataIndex: 'previousYearNetChange',
    key: 'previousYearNetChange',
    render: renderValue,
    title: 'Previous Year Net Change',
  },
  {
    dataIndex: 'yoyNewChange',
    key: 'yoyNewChange',
    render: renderValue,
    title: 'YoY New Change',
  },
  {
    dataIndex: 'yoyClosedChange',
    key: 'yoyClosedChange',
    render: renderValue,
    title: 'YoY Closed Change',
  },
  {
    dataIndex: 'yoyNetChange',
    key: 'yoyNetChange',
    render: renderValue,
    title: 'YoY Net Change',
  },
];

export const getNewClosedProjectsReportRowKey = (
  record: NewClosedProjectsPeriod,
): string =>
  [record.period, record.periodStart, record.periodEnd]
    .filter(Boolean)
    .join('-');
